import { Add, Delete } from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";

import { updatePlayoffBucket } from "../../../apis/Playoff";
import { showToast } from "../../../utils/toast";
import { FormDrawer } from "../../Form/FormDrawer";
import FormWrapper from "../../Form/FormWrapper";
import { PlayoffSearchDrawer } from "./PlayoffSearchDrawer";

export const PlayoffBucketPlayoffDrawer = ({
  bucket,
  setEditOpen,
  reloadTable,
  setLoading,
}) => {
  // =========================
  // State
  // =========================

  const [openPlayoffSearchDrawer, setOpenPlayoffSearchDrawer] = useState(false);
  const [selectedPlayoffs, setSelectedPlayoffs] = useState([]);

  // =========================
  // Effects
  // =========================

  useEffect(() => {
    if (bucket) {
      setSelectedPlayoffs(bucket.playoffs || []);
    }
  }, [bucket]);

  // =========================
  // Handlers
  // =========================

  const handleRemovePlayoff = (playoffId) => {
    setSelectedPlayoffs((prev) =>
      prev.filter((playoff) => playoff.id !== playoffId),
    );
  };

  const handleSave = async () => {
    setLoading(true);

    try {
      await updatePlayoffBucket(bucket.id, {
        playoff_ids: selectedPlayoffs.map((playoff) => playoff.id),
      });

      await reloadTable();

      showToast("PlayOffs updated successfully");

      setEditOpen(false);
    } catch (error) {
      console.error(error);

      showToast("Failed to update PlayOffs", "error");
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // Render
  // =========================

  return (
    <>
      <FormWrapper sx={{ p: 5 }}>
        {/* Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Typography variant="h6">Add PlayOffs to Bucket</Typography>

          <Button variant="contained" onClick={handleSave}>
            Save
          </Button>
        </Box>

        {/* Bucket Details */}
        <Box mb={3}>
          <TextField
            fullWidth
            label="Bucket Name"
            value={bucket?.name || ""}
            InputProps={{
              readOnly: true,
            }}
          />
        </Box>

        <Box mb={3}>
          <TextField
            fullWidth
            label="Status"
            value={bucket?.is_active ? "Active" : "Inactive"}
            InputProps={{
              readOnly: true,
            }}
          />
        </Box>

        {/* PlayOff Section */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6">
            PlayOffs ({selectedPlayoffs.length})
          </Typography>

          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setOpenPlayoffSearchDrawer(true)}
          >
            Add PlayOffs
          </Button>
        </Box>

        {/* Selected PlayOffs */}
        {selectedPlayoffs.length > 0 && (
          <ImageList cols={3} gap={8}>
            {selectedPlayoffs.map((playoff) => (
              <ImageListItem
                key={playoff.id}
                sx={{
                  "& .MuiImageListItem-img": {
                    height: 220,
                    borderRadius: 1,
                    objectFit: "cover",
                  },
                }}
              >
                <img
                  src={
                    playoff?.image?.file ||
                    "https://via.placeholder.com/300x300?text=No+Image"
                  }
                  alt={playoff.title}
                  loading="lazy"
                />

                <ImageListItemBar
                  title={
                    <Tooltip title={playoff.title}>
                      <span
                        style={{
                          display: "block",
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {playoff.title}
                      </span>
                    </Tooltip>
                  }
                  actionIcon={
                    <IconButton
                      sx={{ color: "#fff" }}
                      onClick={() => handleRemovePlayoff(playoff.id)}
                    >
                      <Delete />
                    </IconButton>
                  }
                />
              </ImageListItem>
            ))}
          </ImageList>
        )}
      </FormWrapper>

      {/* Search PlayOff Drawer */}
      <FormDrawer
        open={openPlayoffSearchDrawer}
        setEditOpen={setOpenPlayoffSearchDrawer}
      >
        <PlayoffSearchDrawer
          bucket={bucket}
          selectedPlayoffs={selectedPlayoffs}
          setSelectedPlayoffs={setSelectedPlayoffs}
          setEditOpen={setOpenPlayoffSearchDrawer}
        />
      </FormDrawer>
    </>
  );
};
